import React, { useState, useEffect } from "react";
import { Menu, X, Zap, ArrowRight } from "./Icons";

const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/images/logo-d-192.png`;

const NAV_LINKS = [
  { id: "home",      label: "Home",          url: "/" },
  { id: "hosting",   label: "Get Featured",  url: "/hosting" },
  { id: "ecosystem", label: "Our Products",  url: "/ecosystem" },
  { id: "about",     label: "Our Studio",    url: "/about" },
  { id: "careers",   label: "Join the Core", url: "/careers" },
  { id: "faq",       label: "Engine Room",   url: "/faq" },
];

function Logo({ onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2.5 group" aria-label="Dowble home">
      <img
        src={LOGO_SRC}
        alt="Dowble"
        width="36"
        height="36"
        className="w-9 h-9 rounded-xl object-cover shadow-glow ring-1 ring-zinc-900/5 transition-transform duration-300 group-hover:scale-105"
      />
      <span className="text-zinc-900 font-bold text-lg tracking-tight">Dowble</span>
    </button>
  );
}

export default function Navbar({ activePage, navigate }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleNav = (id) => {
    navigate(id);
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-zinc-200 shadow-[0_8px_24px_-16px_rgba(16,24,40,0.18)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
        <Logo onClick={() => handleNav("home")} />

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => {
            const active = activePage === link.id;
            return (
              <li key={link.id}>
                <button
                  onClick={() => handleNav(link.id)}
                  aria-current={active ? "page" : undefined}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    active ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-accent-400 to-emerald-400 origin-left transition-transform duration-300 ${
                      active ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => handleNav("contact")} className="btn btn-primary btn-md">
            <Zap size={14} />
            Launch Project
          </button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden btn-icon"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-zinc-200 px-6 pb-6 animate-slide-down shadow-sm">
          <ul className="flex flex-col gap-1 pt-2">
            {NAV_LINKS.map((link) => {
              const active = activePage === link.id;
              return (
                <li key={link.id}>
                  <button
                    onClick={() => handleNav(link.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      active
                        ? "text-zinc-900 bg-zinc-100 border border-zinc-200"
                        : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
                    }`}
                  >
                    {link.label}
                  </button>
                </li>
              );
            })}
            <li className="pt-3">
              <button
                onClick={() => handleNav("contact")}
                className="btn btn-primary w-full py-3"
              >
                Launch Project
                <ArrowRight size={16} />
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
